import React, { useMemo, useState } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useAuth } from '../auth/AuthContext';
import { setRuntimeBrandColors, getContrastText, getReadableColor, getGradientContrastText } from '../../utils/brandPalette';
import { companyMediaCacheKey, getCompanyLogoUrl, resolveCompanyId } from '../../utils/companyIdentity';

const DEFAULTS = {
    primary: '#2E6F8E',
    secondary: '#42B89B',
    accent: '#7C5CF2',
};

const normalizeHex = (value, fallback) => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed;
    if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
        return `#${trimmed.slice(1).split('').map((ch) => ch + ch).join('')}`;
    }
    return fallback;
};

const hexToRgb = (hex) => {
    const safe = normalizeHex(hex, '#000000').slice(1);
    return `${parseInt(safe.slice(0, 2), 16)}, ${parseInt(safe.slice(2, 4), 16)}, ${parseInt(safe.slice(4, 6), 16)}`;
};

const resolveColors = (source = {}) => {
    const branding = source?.branding || source?.theme || {};
    return {
        primary: normalizeHex(source?.primaryColor || branding.primaryColor || branding.primary, DEFAULTS.primary),
        secondary: normalizeHex(source?.secondaryColor || branding.secondaryColor || branding.secondary, DEFAULTS.secondary),
        accent: normalizeHex(source?.accentColor || branding.accentColor || branding.accent, DEFAULTS.accent),
    };
};

const applyCssTheme = (source, colors) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-rgb', hexToRgb(colors.primary));
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--secondary-rgb', hexToRgb(colors.secondary));
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--accent-rgb', hexToRgb(colors.accent));
    root.style.setProperty('--primary-contrast', getContrastText(colors.primary));
    root.style.setProperty('--secondary-contrast', getContrastText(colors.secondary));
    root.style.setProperty('--accent-contrast', getContrastText(colors.accent));
    root.style.setProperty('--primary-ink', getReadableColor(colors.primary));
    root.style.setProperty('--secondary-ink', getReadableColor(colors.secondary));
    root.style.setProperty('--accent-ink', getReadableColor(colors.accent));
    root.style.setProperty('--brand-action-text', getGradientContrastText(colors.primary, colors.secondary));
    const companyId = resolveCompanyId(source);
    const companyLogo = getCompanyLogoUrl(source);
    const companyName = source?.companyName || source?.name || '';
    root.style.setProperty('--company-logo', `url("${companyLogo}")`);
    root.style.setProperty('--company-name', `"${String(companyName).replace(/"/g, '\"')}"`);
    if (typeof window !== 'undefined') {
        if (companyId) {
            const logoKey = companyMediaCacheKey(companyId, 'logo');
            if (companyLogo) window.localStorage.setItem(logoKey, companyLogo);
            else window.localStorage.removeItem(logoKey);
        }
        if (companyId && companyName) window.localStorage.setItem(companyMediaCacheKey(companyId, 'name'), companyName);
    }
    document.body.dataset.erpTheme = 'command-center';
};

const ThemeRuntime = ({ children }) => {
    const { companyData } = useAuth();
    const [platformTheme, setPlatformTheme] = useState(DEFAULTS);

    React.useEffect(() => {
        let active = true;
        const loadPlatformTheme = async () => {
            try {
                const runtime = await getDoc(doc(db, 'systemSettings', 'public_runtime'));
                if (!active) return;
                const data = runtime.exists() ? runtime.data() || {} : {};
                setPlatformTheme(resolveColors(data?.theme || data?.branding || data));
            } catch (error) {
                console.warn('Platform theme unavailable; using TELCOFAIR defaults:', error?.message || error);
                if (active) setPlatformTheme(DEFAULTS);
            }
        };
        const refresh = (event) => {
            const next = event?.detail;
            if (next && typeof next === 'object') setPlatformTheme(resolveColors(next));
            else loadPlatformTheme();
        };
        const runtimeRef = doc(db, 'systemSettings', 'public_runtime');
        const unsubscribeRuntime = onSnapshot(runtimeRef, (snapshot) => {
            if (!active || !snapshot.exists()) return;
            const data = snapshot.data() || {};
            setPlatformTheme(resolveColors(data?.theme || data?.branding || data));
        }, (error) => console.warn('Platform theme realtime update unavailable:', error?.message || error));
        loadPlatformTheme();
        window.addEventListener('telcofair:platform-theme-updated', refresh);
        window.addEventListener('telcofair:platform-branding-updated', loadPlatformTheme);
        return () => {
            active = false;
            unsubscribeRuntime?.();
            window.removeEventListener('telcofair:platform-theme-updated', refresh);
            window.removeEventListener('telcofair:platform-branding-updated', loadPlatformTheme);
        };
    }, []);

    // Theme is platform-owned. Company data contributes only company identity/logo,
    // never its own primary/secondary/accent palette.
    const source = useMemo(() => ({ ...(companyData || {}), ...platformTheme }), [companyData, platformTheme]);
    const colors = useMemo(() => ({
        primary: normalizeHex(platformTheme.primary, DEFAULTS.primary),
        secondary: normalizeHex(platformTheme.secondary, DEFAULTS.secondary),
        accent: normalizeHex(platformTheme.accent, DEFAULTS.accent),
    }), [platformTheme]);

    // Update the JS palette before descendants render. This is what makes
    // page-level MUI `sx` values react to Company Settings instead of only CSS.
    setRuntimeBrandColors(colors);
    applyCssTheme(source, colors);

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: 'light',
            primary: { main: colors.primary, contrastText: getContrastText(colors.primary) },
            secondary: { main: colors.secondary, contrastText: getContrastText(colors.secondary) },
            info: { main: colors.accent, contrastText: getContrastText(colors.accent) },
            success: { main: '#16A36A' },
            warning: { main: '#E29A20' },
            error: { main: '#D94B5A' },
            background: { default: '#F1F6F9', paper: '#FFFFFF' },
            text: { primary: '#182235', secondary: '#6D7C91' },
        },
        typography: {
            fontFamily: "'Plus Jakarta Sans','Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
            button: { textTransform: 'none', fontWeight: 700 },
        },
        shape: { borderRadius: 12 },
        shadows: [
            'none',
            '0 2px 8px rgba(25,57,78,.05)',
            '0 6px 18px rgba(25,57,78,.07)',
            '0 10px 28px rgba(25,57,78,.09)',
            '0 14px 34px rgba(25,57,78,.11)',
            ...Array(20).fill('0 16px 42px rgba(25,57,78,.12)'),
        ],
        components: {
            MuiButton: {
                styleOverrides: {
                    root: { borderRadius: 10, minHeight: 34, boxShadow: 'none' },
                    containedPrimary: {
                        color: getGradientContrastText(colors.primary, colors.secondary),
                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                        boxShadow: `0 9px 22px rgba(${hexToRgb(colors.primary)},.22)`,
                    },
                    textPrimary: {
                        color: getReadableColor(colors.primary),
                    },
                    outlinedPrimary: {
                        color: getReadableColor(colors.primary),
                        borderColor: `rgba(${hexToRgb(colors.primary)},.26)`,
                    },
                    containedSecondary: {
                        color: getContrastText(colors.secondary),
                        backgroundColor: colors.secondary,
                    },
                    textSecondary: {
                        color: getReadableColor(colors.secondary),
                    },
                    outlinedSecondary: {
                        color: getReadableColor(colors.secondary),
                        borderColor: `rgba(${hexToRgb(colors.secondary)},.26)`,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 11,
                        background: 'rgba(255,255,255,.94)',
                        color: '#182235',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,.92)',
                        '& fieldset': { borderColor: 'rgba(31,62,82,.12)' },
                        '&:hover fieldset': { borderColor: `rgba(${hexToRgb(colors.primary)},.28)` },
                        '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1.5 },
                    },
                    input: { color: '#182235', fontWeight: 550 },
                },
            },
            MuiInputLabel: {
                styleOverrides: { root: { color: '#6D7C91', fontWeight: 650, '&.Mui-focused': { color: getReadableColor(colors.primary) } } },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 16,
                        border: '1px solid rgba(31,62,82,.085)',
                        boxShadow: '0 10px 30px rgba(25,57,78,.085), inset 0 1px 0 rgba(255,255,255,.9)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: { borderRadius: 16 },
                },
            },
            MuiDialog: {
                styleOverrides: { paper: { borderRadius: 20, border: '1px solid rgba(31,62,82,.09)', boxShadow: '0 26px 80px rgba(18,45,64,.20)' } },
            },
            MuiDialogTitle: { styleOverrides: { root: { fontWeight: 850, color: '#182235' } } },
            MuiTabs: {
                styleOverrides: { indicator: { height: 3, borderRadius: 999, background: colors.primary } },
            },
            MuiTab: {
                styleOverrides: { root: { minHeight: 42, textTransform: 'none', fontWeight: 750, color: '#6D7C91', '&.Mui-selected': { color: getReadableColor(colors.primary) } } },
            },
            MuiChip: {
                styleOverrides: { root: { fontWeight: 750, borderRadius: 999 }, label: { lineHeight: 1.2 } },
            },
            MuiTableCell: {
                styleOverrides: { head: { fontSize: '.69rem', fontWeight: 850, color: '#425168', background: '#F7FAFC' }, body: { color: '#243247', borderColor: 'rgba(31,62,82,.07)' } },
            },
            MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, fontSize: '.68rem', background: '#172235' } } },
        },
    }), [colors]);

    const themeKey = `${colors.primary}-${colors.secondary}-${colors.accent}`;

    return (
        <ThemeProvider theme={muiTheme} key={themeKey}>
            <CssBaseline />
            {children}
        </ThemeProvider>
    );
};

export default ThemeRuntime;
